// Translation utility for managing UI metadata
import enTranslations from '../data/translations/en.json';
import arTranslations from '../data/translations/ar.json';
import urTranslations from '../data/translations/ur.json';
import maTranslations from '../data/translations/ma.json';

const translations = {
  en: enTranslations,
  ar: arTranslations,
  ur: urTranslations,
  ma: maTranslations
};

/**
 * Get translated text for a given key and language
 * @param {string} language - Language code (en, ar, ur, ma)
 * @param {string} key - Translation key in dot notation (e.g., 'ui.navigation.next')
 * @param {object} fallback - Fallback value if translation not found
 * @returns {string} Translated text
 */
export const getTranslation = (language, key, fallback = '') => {
  try {
    const languageData = translations[language] || translations.en;
    const keys = key.split('.');
    let value = languageData;
    
    for (const k of keys) {
      if (value && typeof value === 'object' && k in value) {
        value = value[k];
      } else {
        // If key not found, try English as fallback
        if (language !== 'en' && translations.en) {
          return getTranslation('en', key, fallback);
        }
        return fallback;
      }
    }
    
    return typeof value === 'string' ? value : fallback;
  } catch (error) {
    console.warn(`Translation error for key "${key}" in language "${language}":`, error);
    return fallback;
  }
};

/**
 * Get all translations for a specific section
 * @param {string} language - Language code
 * @param {string} section - Section key (e.g., 'ui.navigation')
 * @returns {object} Section translations
 */
export const getTranslationSection = (language, section) => {
  try {
    const languageData = translations[language] || translations.en;
    const keys = section.split('.');
    let value = languageData;
    
    for (const k of keys) {
      if (value && typeof value === 'object' && k in value) {
        value = value[k];
      } else {
        return {};
      }
    }
    
    return typeof value === 'object' ? value : {};
  } catch (error) {
    console.warn(`Translation section error for "${section}" in language "${language}":`, error);
    return {};
  }
};

/**
 * Hook-like function to get translations for current language
 * @param {string} currentLanguage - Current language code
 * @returns {function} Translation function
 */
export const useTranslations = (currentLanguage) => {
  return {
    t: (key, fallback = '') => getTranslation(currentLanguage, key, fallback),
    getSection: (section) => getTranslationSection(currentLanguage, section)
  };
};

// Available languages configuration
export const AVAILABLE_LANGUAGES = [
  { code: 'en', name: 'English', nativeName: 'English' },
  { code: 'ar', name: 'Arabic', nativeName: 'العربية' },
  { code: 'ur', name: 'Urdu', nativeName: 'اردو' },
  { code: 'ma', name: 'Malayalam', nativeName: 'മലയാളം' }
];

const translationUtils = {
  getTranslation,
  getTranslationSection,
  useTranslations,
  AVAILABLE_LANGUAGES
};

export default translationUtils;
